import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';

const StatsSection = ({ score, highScore }) => {
  const [stats, setStats] = useState({
    gamesPlayed: 0,
    totalScore: 0,
    averageScore: 0,
    linesCleared: 0,
    playtime: 0, // in minutes
    bestStreak: 0,
    shapesPlaced: 0
  });

  // Load stats from localStorage on component mount
  useEffect(() => {
    const savedStats = localStorage.getItem('gameStats');
    if (savedStats) {
      try {
        setStats(JSON.parse(savedStats));
      } catch (error) {
        console.error('Error loading stats:', error);
      }
    }
  }, []);

  // Update stats when game ends (when score resets to 0 from a higher value)
  useEffect(() => {
    const savedScore = parseInt(localStorage.getItem('lastScore') || '0');
    
    if (savedScore > 0 && score === 0) {
      // Game ended, update stats
      const newStats = {
        ...stats,
        gamesPlayed: stats.gamesPlayed + 1,
        totalScore: stats.totalScore + savedScore,
        averageScore: Math.round((stats.totalScore + savedScore) / (stats.gamesPlayed + 1))
      };
      
      setStats(newStats);
      localStorage.setItem('gameStats', JSON.stringify(newStats));
    }
    
    localStorage.setItem('lastScore', score.toString());
  }, [score, stats]);

  const formatTime = (minutes) => {
    if (minutes < 60) return `${minutes}m`;
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours}h ${mins}m`;
  };

  const resetStats = () => {
    const emptyStats = {
      gamesPlayed: 0,
      totalScore: 0,
      averageScore: 0,
      linesCleared: 0,
      playtime: 0,
      bestStreak: 0,
      shapesPlaced: 0
    };
    setStats(emptyStats);
    localStorage.setItem('gameStats', JSON.stringify(emptyStats));
    localStorage.removeItem('lastScore');
  };

  return (
    <div className="stats-section">
      <h3 className="panel-group-title">Game Statistics</h3>
      
      <div className="stats-grid">
        <div className="stat-item">
          <div className="stat-value" aria-label="High Score value">{highScore.toLocaleString()}</div>
          <div className="stat-label">High Score</div>
        </div>
        
        <div className="stat-item">
          <div className="stat-value" aria-label="Games Played value">{stats.gamesPlayed}</div>
          <div className="stat-label">Games Played</div>
        </div>
        
        <div className="stat-item">
          <div className="stat-value" aria-label="Average Score value">{stats.averageScore.toLocaleString()}</div>
          <div className="stat-label">Average Score</div>
        </div>
        
        <div className="stat-item">
          <div className="stat-value" aria-label="Total Score value">{stats.totalScore.toLocaleString()}</div>
          <div className="stat-label">Total Score</div>
        </div>
        
        <div className="stat-item">
          <div className="stat-value" aria-label="Lines Cleared value">{stats.linesCleared}</div>
          <div className="stat-label">Lines Cleared</div>
        </div>
        
        <div className="stat-item">
          <div className="stat-value" aria-label="Playtime value">{formatTime(stats.playtime)}</div>
          <div className="stat-label">Playtime</div>
        </div>
      </div>
      
      <button 
        className="menu-btn reset-stats-btn" 
        onClick={resetStats}
        title="Reset all statistics"
      >
        <span className="btn-icon" aria-hidden="true">🔄</span> Reset Stats
      </button>
    </div>
  );
};

StatsSection.propTypes = {
  score: PropTypes.number.isRequired,
  highScore: PropTypes.number.isRequired
};

export default React.memo(StatsSection);